import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ICONS } from '../components/icons/registry'
import CalendlyButton from '../components/CalendlyButton'
import SEO from '../components/SEO'
import Breadcrumbs from '../components/Breadcrumbs'
import { generateServiceSchema, generateLocalBusinessSchema, generateFAQSchema } from '../lib/structured-data-utils'
import CtaSection from '../components/CtaSection'

const KubernetesConsultingDubai = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  /*
    Icons come from the registry (DMX-045), which is the one place that decides which
    icon means what. Kubernetes itself is the Linux Foundation's brand mark, so it is a
    static SVG and takes no dark treatment; the other three are Lottie artwork and pass
    darkTreatment="none", because the default dark treatment flattens an icon into a
    solid white silhouette. The element is built here rather than at the render site so
    the static mark and the animated ones can differ, as they do on the About page.
  */
  const services = [
    {
      id: 'k8s-architecture',
      title: 'Kubernetes Architecture Design',
      description: 'Design scalable, secure, and highly available Kubernetes clusters tailored to your workload requirements. We help you choose the right architecture patterns for your Dubai business needs.',
      icon: <ICONS.kubernetes className="w-full h-full" />,
      features: [
        'Multi-cluster Architecture Design',
        'High Availability Configuration',
        'Network Policy & Security Design',
        'Resource Quota Planning',
        'Storage Class Configuration',
        'Disaster Recovery Planning'
      ]
    },
    {
      id: 'k8s-migration',
      title: 'Container Migration',
      description: 'Migrate your legacy applications to containers and Kubernetes with minimal disruption. We handle the entire journey from containerization to production deployment.',
      icon: <ICONS.migration className="w-full h-full" darkTreatment="none" />,
      features: [
        'Application Containerization',
        'Docker Image Optimization',
        'Helm Chart Development',
        'Migration Strategy Planning',
        'Zero-downtime Migration',
        'Legacy System Integration'
      ]
    },
    {
      id: 'managed-k8s',
      title: 'Managed Kubernetes Services',
      description: 'Deploy and manage Kubernetes on AWS EKS, Azure AKS, or Google GKE. We optimize your managed Kubernetes environment for performance, security, and cost efficiency.',
      icon: <ICONS.managedService className="w-full h-full" darkTreatment="none" />,
      features: [
        'EKS/AKS/GKE Setup & Configuration',
        'Node Pool Optimization',
        'Auto-scaling Configuration',
        'Cost Management & Optimization',
        'Cluster Upgrades & Maintenance',
        'Multi-cloud Kubernetes Strategy'
      ]
    },
    {
      id: 'k8s-security',
      title: 'Kubernetes Security',
      description: 'Secure your Kubernetes clusters with industry best practices. We implement defense-in-depth strategies to protect your containerized workloads from threats.',
      icon: <ICONS.security className="w-full h-full" darkTreatment="none" />,
      features: [
        'RBAC Policy Implementation',
        'Pod Security Standards',
        'Network Policy Configuration',
        'Secrets Management (Vault)',
        'Container Image Scanning',
        'Runtime Security Monitoring'
      ]
    }
  ]

  const useCases = [
    {
      title: 'Microservices Architecture',
      description: 'Deploy and manage complex microservices applications with service mesh integration, traffic management, and observability.',
      Icon: ICONS.microservices
    },
    {
      title: 'Scalable Web Applications',
      description: 'Run web applications that automatically scale based on traffic demands, ensuring performance during peak loads.',
      Icon: ICONS.webApplications
    },
    {
      title: 'Machine Learning Pipelines',
      description: 'Orchestrate ML training and inference workloads with GPU support and efficient resource scheduling.',
      Icon: ICONS.ai
    },
    {
      title: 'CI/CD Workloads',
      description: 'Run build agents, test environments, and deployment pipelines in isolated, reproducible containers.',
      Icon: ICONS.cicdPipeline
    },
    {
      title: 'Stateful Applications',
      description: 'Deploy databases, message queues, and other stateful workloads with persistent storage and backup strategies.',
      Icon: ICONS.database
    },
    {
      title: 'Multi-tenant Platforms',
      description: 'Build SaaS platforms with namespace isolation, resource quotas, and tenant-specific configurations.',
      Icon: ICONS.enterprise
    }
  ]

  const technologies = [
    { name: 'Kubernetes', logo: 'K8s' },
    { name: 'Helm', logo: 'Helm' },
    { name: 'Istio', logo: 'Mesh' },
    { name: 'ArgoCD', logo: 'GitOps' },
    { name: 'Prometheus', logo: 'Monitor' },
    { name: 'Grafana', logo: 'Visualize' },
    { name: 'Vault', logo: 'Secrets' },
    { name: 'Kustomize', logo: 'Config' }
  ]

  const faqs = [
    {
      question: 'What Kubernetes consulting services do you offer in Dubai?',
      answer: 'We offer end-to-end Kubernetes consulting in Dubai including cluster architecture design, container migration, managed Kubernetes setup on AWS EKS, Azure AKS, and Google GKE, Kubernetes security hardening, monitoring with Prometheus and Grafana, and GitOps implementation with ArgoCD. We work with startups, SMEs, and enterprises across the UAE.'
    },
    {
      question: 'How much does Kubernetes consulting cost in Dubai?',
      answer: 'Kubernetes consulting rates in Dubai typically range from AED 600 to AED 1,800 per hour depending on the complexity of the engagement. A basic EKS or AKS cluster setup starts from AED 30,000, while a full Kubernetes migration with security hardening and monitoring ranges from AED 80,000 to AED 300,000. We offer fixed-price projects, time and materials, and monthly retainer models.'
    },
    {
      question: 'How long does it take to set up a production Kubernetes cluster?',
      answer: 'A production-ready Kubernetes cluster with high availability, monitoring, and security can be set up in 3 to 6 weeks. A basic development cluster takes 1 to 2 weeks. Full container migration of existing applications typically takes 6 to 12 weeks depending on the number of services and complexity of the workloads being migrated.'
    },
    {
      question: 'Should I use AWS EKS, Azure AKS, or Google GKE for my Dubai business?',
      answer: 'The best managed Kubernetes service depends on your existing cloud provider and workload requirements. AWS EKS is ideal if you are already invested in the AWS ecosystem and need tight integration with services like RDS, S3, and IAM. Azure AKS works well for Microsoft-centric organizations. Google GKE offers the most mature Kubernetes experience with features like Autopilot mode. We help you evaluate and choose the right platform based on your specific needs.'
    },
    {
      question: 'Can you migrate our existing applications to Kubernetes?',
      answer: 'Yes, container migration is one of our core Kubernetes services. We handle the full migration lifecycle including application assessment, Dockerfile creation and optimization, Helm chart development, CI/CD pipeline integration, and zero-downtime cutover. We specialize in migrating monolithic applications to microservices and containerizing legacy workloads running on virtual machines.'
    },
    {
      question: 'What industries do you serve with Kubernetes consulting in Dubai?',
      answer: 'We serve a wide range of industries in Dubai and the UAE including fintech and banking, e-commerce and retail, healthcare and medtech, logistics and supply chain, SaaS companies, and government organizations. Our Kubernetes implementations are tailored to meet industry-specific compliance and data residency requirements in the UAE.'
    },
    {
      question: 'Do you provide ongoing Kubernetes support and management?',
      answer: 'Yes, we offer managed Kubernetes services including 24/7 cluster monitoring, incident response, version upgrades, security patching, cost optimization, and capacity planning. Many of our Dubai clients start with a migration project and transition to a monthly managed services retainer starting from AED 8,000 per month depending on cluster size and complexity.'
    },
    {
      question: 'What is the difference between Kubernetes and Docker?',
      answer: 'Docker is a containerization tool that packages applications into portable containers. Kubernetes is a container orchestration platform that manages, scales, and deploys those containers across clusters of machines. Docker handles individual containers while Kubernetes manages fleets of containers with features like auto-scaling, load balancing, self-healing, and rolling deployments. Most production environments use both Docker and Kubernetes together.'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const serviceSchemas = services.map(service => generateServiceSchema({
    name: `${service.title} in Dubai`,
    description: service.description,
    provider: 'Demirix',
    areaServed: 'Dubai, UAE'
  }))

  const localBusinessSchema = generateLocalBusinessSchema()
  const faqSchema = generateFAQSchema(faqs)
  const structuredData = [...serviceSchemas, localBusinessSchema, faqSchema]

  const breadcrumbs = [
    { name: 'Services', path: '/services' },
    { name: 'Kubernetes Consulting Dubai', path: '/kubernetes-consulting-dubai' }
  ]

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="Kubernetes Consulting Dubai | Container Orchestration & K8s Services UAE"
        description="Expert Kubernetes consulting in Dubai. We design, deploy, and manage Kubernetes clusters on AWS EKS, Azure AKS, and GCP GKE. Container orchestration services for UAE businesses."
        keywords={[
          'kubernetes consulting dubai',
          'kubernetes services uae',
          'k8s consulting dubai',
          'container orchestration dubai',
          'eks consulting dubai',
          'aks consulting uae',
          'gke consulting dubai',
          'docker kubernetes dubai',
          'microservices dubai',
          'helm charts dubai',
          'kubernetes migration uae',
          'container security dubai'
        ]}
        url={currentUrl}
        structuredData={structuredData}
      />
      <Breadcrumbs customCrumbs={breadcrumbs} />

      {/* Hero Section */}
      <section className="min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium mb-6">
              Container Orchestration
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Kubernetes Consulting in Dubai
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              Unlock the power of container orchestration with expert Kubernetes consulting.
              We help Dubai businesses design, deploy, and manage production-grade Kubernetes
              clusters that scale with your growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CalendlyButton className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 w-full sm:w-auto">
                Schedule K8s Consultation
              </CalendlyButton>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-primary-600 dark:bg-primary-700">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              { stat: '50+', label: 'K8s Clusters Deployed' },
              { stat: '99.99%', label: 'Uptime Achieved' },
              { stat: '60%', label: 'Cost Reduction' },
              { stat: '24/7', label: 'Cluster Monitoring' }
            ].map((item, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{item.stat}</div>
                <div className="text-primary-100">{item.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Our Kubernetes Services
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Comprehensive Kubernetes solutions from initial architecture design to
                production deployment and ongoing management.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service) => (
                <motion.div
                  key={service.id}
                  variants={itemVariants}
                  className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 flex-shrink-0" aria-hidden="true">
                      {service.icon}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        {service.description}
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Kubernetes Use Cases
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Kubernetes powers a wide range of workloads. Here's how we help Dubai businesses leverage container orchestration.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600"
                >
                  <div className="w-12 h-12 mb-4" aria-hidden="true">
                    <useCase.Icon className="w-full h-full" darkTreatment="none" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {useCase.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {useCase.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Our K8s Technology Stack
              </h2>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4">
              {technologies.map((tech, index) => (
                <div
                  key={index}
                  className="px-6 py-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow text-center"
                >
                  <div className="text-xs text-primary-600 dark:text-primary-400 font-medium mb-1">{tech.logo}</div>
                  <div className="font-semibold text-gray-900 dark:text-white">{tech.name}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Common questions about Kubernetes consulting services in Dubai answered by our engineering team.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 border border-gray-100 dark:border-gray-600"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cross-link Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Related Services
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/devops-services-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                DevOps Services
              </Link>
              <Link
                to="/aws-cloud-consulting-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                AWS Cloud Consulting
              </Link>
              <Link
                to="/devsecops-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                DevSecOps Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        heading="Ready to Containerize Your Applications?"
        description="Let's discuss how Kubernetes can help your Dubai business achieve better scalability, reliability, and operational efficiency."
        primary={{ kind: 'calendly', label: 'Schedule Free Consultation' }}
        secondary={{ kind: 'link', label: 'Contact Us', href: '/contact' }}
      />
    </div>
  )
}

export default KubernetesConsultingDubai
